'use client';

import type { ReactNode } from 'react';
import { useState } from 'react';
import { Streamdown } from 'streamdown';
import { getToolIcon } from '@/lib/tool-display';
import { countToolWorkUnits, describeToolPart, synthesizeToolSummary } from '@/lib/timeline-synthesis';
import { resolveDelegatedToolDisplay } from '@/lib/capabilities/display';
import { CodeBlock } from './code-block';
import {
  CaretDown,
  MagnifyingGlass,
  Image as ImageIcon,
  SpeakerHigh,
  MusicNote,
  WaveTriangle,
  Brain,
  Eye,
  BookOpen,
  Clock,
  Sparkle,
  FileCode,
  CheckCircle,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

/**
 * Tools whose output renders a visual artifact the user interacts with directly.
 * These stay visible as cards in the chat — EXCEPT the agent-work subset in
 * lib/timeline-synthesis's COLLAPSIBLE_WORK_TOOLS (sandbox exec runs, file
 * reads, source greps): once completed, those fold into the aggregated
 * timeline entry, carrying their original card into its expansion.
 */
export const RENDERED_TOOLS = new Set<string>([
  'svg',
  'html',
  'htmlNative',
  'mermaid',
  'remotion',
  // hyperframes / createSceneVideo are 'artifact-preferred' in
  // tool-visualization-renderer's DEFAULT_PRESENTATION_BY_TOOL (panel-docked
  // media) but were missing here, so they fell through to isUtilityTool and
  // got folded into the utility group with NO card — the exact "buried
  // video deliverable" bug (CHAT_UI_DESIGN_REVIEW.md §4 item 5): once the
  // panel closed, the only trace was a "Video clip generated · N STEPS"
  // timeline line over raw JSON, no re-open affordance. Listing them here
  // routes them through the same artifact-candidate flow as every other
  // rendered tool (persistent "Open"/"Show inline" chip included).
  'hyperframes',
  'createSceneVideo',
  'p5',
  'scene3d',
  'molecule3d',
  'flowchart',
  'timeline',
  'comparisonTable',
  'conceptMap',
  'cycleDiagram',
  'hierarchy',
  'graph',
  'generateImage', // the single image viewer is a rendered artifact
  'generateImageBatch',
  'generateSpeech',
  'generateNarrationPlan',
  'generateMusic',
  'generateSoundEffect',
  'generateSoundEffectBatch',
  'viewMediaAsset',
  'saveMediaAssetFromUrl',
  'promoteGeneratedMediaToAsset',
  'sandboxExec',
  'sandboxCodeRun',
  'sandboxSessionExec',
  'sandboxSessionLogs',
  'sandboxListFiles',
  'sandboxReadFile',
  'browserListFiles',
  'browserReadFile',
  'readGalexiaFile',
  'readGalexiaFileLines',
  'listMyDocuments',
  'viewDocument',
  'getVisualizationSource',
  'readVisualizationLines',
  'grepVisualization',
  // Trigger.dev background-video tools — output is a live PendingNarratedVideo
  // card that must stay visible (never collapse into the utility summary).
  'createNarratedVideo',
  'createParallelNarratedVideo',
  'createNarratedVideoBatch',
  // Dashboard tools — each call opens / refreshes the side artifact panel.
  // If they collapse into the utility group, the chat-side affordance to
  // RE-OPEN that panel disappears: once the user closes the panel, the
  // only entry point back is buried behind an N-steps disclosure. Keep
  // them visible so the dashboard tile stays one click away.
  'create_dashboard',
  'update_dashboard',
  'show_dashboard',
  'requestConnection',
  // askUserQuestion — the question card with answer chips must stay visible
  // so the user can actually answer it; never fold into the utility group.
  'askUserQuestion',
]);

/**
 * Utility tools (search, memory, asset prep for videos, etc.) — these collapse
 * into a ChainOfThought-style group once complete so the chat stays clean.
 */
export function isUtilityTool(toolName: string, input?: unknown, output?: unknown): boolean {
  const delegated = resolveDelegatedToolDisplay(toolName, input, output);
  if (delegated.presentation === 'artifact-preferred' || delegated.presentation === 'artifact-only') {
    return false;
  }
  return !RENDERED_TOOLS.has(delegated.toolName);
}

// =============================================================================
// Per-tool icon + summary builders
// =============================================================================

const TOOL_ICONS: Record<string, typeof MagnifyingGlass> = {
  webSearch: MagnifyingGlass,
  imageSearch: ImageIcon,
  imageSearchBatch: ImageIcon,
  generateSpeech: SpeakerHigh,
  generateNarrationPlan: SpeakerHigh,
  generateMusic: MusicNote,
  generateSoundEffect: WaveTriangle,
  generateSoundEffectBatch: WaveTriangle,
  generateImageBatch: ImageIcon,
  listUserMemories: Brain,
  viewFullMemory: Brain,
  createUserMemory: Brain,
  updateUserMemory: Brain,
  deleteUserMemory: Brain,
  listSkills: BookOpen,
  readSkill: BookOpen,
  listVisualizationVersions: Clock,
  getVisualizationSource: FileCode,
  viewVisualization: Eye,
  suggestInteraction: Sparkle,
};

function iconFor(toolName: string): typeof MagnifyingGlass {
  // Local TOOL_ICONS overrides take priority (legacy mappings); fall through
  // to the centralized tool-display registry. New tools should be added to
  // lib/tool-display.ts, not here — this dict will be drained over time.
  if (TOOL_ICONS[toolName]) return TOOL_ICONS[toolName];
  return getToolIcon(toolName) as typeof MagnifyingGlass;
}

/**
 * Human-friendly one-line label for a completed tool part.
 * Delegates to the shared timeline-synthesis phrase rules so the collapsed
 * rollup line and the per-step rows always speak the same language.
 */
function summarize(toolName: string, input?: unknown, output?: unknown): string {
  return describeToolPart({ toolName, input, output });
}

// Bounded: giant tool payloads in the DOM cause layout thrash. The pre is
// height-capped for scrolling, but the string itself must be capped too —
// syntax-highlighting a multi-hundred-KB JSON block hangs the frame.
const DETAILS_MAX_CHARS = 3_000;

function stringifyForDetails(value: unknown) {
  if (value === undefined) return undefined;
  let serialized: string;
  if (typeof value === 'string') {
    serialized = value;
  } else {
    try {
      serialized = JSON.stringify(value, null, 2);
    } catch {
      serialized = String(value);
    }
  }
  if (serialized.length <= DETAILS_MAX_CHARS) return serialized;
  return `${serialized.slice(0, DETAILS_MAX_CHARS)}\n… truncated (${serialized.length.toLocaleString()} chars total)`;
}

// =============================================================================
// Group component
// =============================================================================

interface CompletedTool {
  key: string;
  toolName: string;
  input?: unknown;
  output?: unknown;
  errored?: boolean;
  /**
   * Optional full card render for this step — the EXACT node the chat would
   * have rendered standalone (terminal output, file viewer, …). When present,
   * expanding the timeline entry shows this card directly instead of the
   * compact input/result JSON drilldown. This is how aggregated work tools
   * (sandbox exec runs, file reads) keep their original renderers.
   */
  card?: ReactNode;
}

interface ReasoningStep {
  key: string;
  text: string;
}

/**
 * A short narration the model emitted BETWEEN tool calls that got folded
 * into the surrounding group (lab-chat-view's grouping pass merges adjacent
 * runs separated only by a plain, sub-150-char text part — see
 * CHAT_UI_DESIGN_REVIEW.md §4 item 2). Rendered as a quiet, non-expandable
 * line — it's not a "Thinking" block, just narration promoted into the
 * timeline instead of splitting it in two.
 */
interface NoteStep {
  key: string;
  text: string;
}

/**
 * Interleaved step — either a completed utility tool call, a reasoning
 * ("Thinking") block, or a folded narration note. Order matters: the group
 * renders steps in temporal order, so callers should pass them in the order
 * the model emitted them.
 */
export type GroupStep =
  | ({ kind: 'tool' } & CompletedTool)
  | ({ kind: 'reasoning' } & ReasoningStep)
  | ({ kind: 'note' } & NoteStep);

export function CompletedToolsGroup({
  tools,
  reasoningSteps,
  steps,
  fullContent,
}: {
  /**
   * Flat tool list (legacy). Used by surfaces that don't track
   * interleaved reasoning. When `steps` is also provided, it wins.
   */
  tools: CompletedTool[];
  /**
   * Reasoning entries to render after the tool list (legacy). Used by
   * surfaces that built a separate reasoning subsection. When `steps`
   * is provided, this is ignored.
   */
  reasoningSteps?: ReasoningStep[];
  /**
   * Interleaved temporal-order list of steps — tools and reasoning
   * mixed in the order the model emitted them. When provided, this
   * supersedes both `tools` and `reasoningSteps` for the rendered
   * step list. Each reasoning step gets its own per-step expander
   * inside the group's expanded view, so users can drill into a
   * single thought block without expanding all of them.
   */
  steps?: GroupStep[];
  /** Optional: the per-tool full output nodes. If present, rendered inside the expander. */
  fullContent?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  // Caret + expand state for individual reasoning steps inside the group.
  // Tools don't have a per-step expander right now (their summary is
  // single-line); only reasoning items toggle individually.
  const [expandedReasoning, setExpandedReasoning] = useState<Record<string, boolean>>({});
  const [expandedTools, setExpandedTools] = useState<Record<string, boolean>>({});

  // Build the canonical interleaved step list. If the caller provided
  // `steps` directly, use that (preserves temporal order). Otherwise
  // synthesize one from the legacy `tools` + `reasoningSteps` props
  // (tools first, then reasoning — the old behaviour).
  const orderedSteps: GroupStep[] = steps
    ? steps
    : [
        ...tools.map((t) => ({ kind: 'tool' as const, ...t })),
        ...(reasoningSteps ?? []).map((r) => ({ kind: 'reasoning' as const, ...r })),
      ];

  const toolSteps = orderedSteps.filter((s): s is Extract<GroupStep, { kind: 'tool' }> => s.kind === 'tool');
  const hasError = toolSteps.some((t) => t.errored);

  // The rollup line — "Project scaffolded · Dependencies installed ·
  // 4 files written · Dev server running". Reasoning/note steps don't
  // contribute a phrase (too verbose for one line) but count toward the
  // step total below.
  const summaryLine = synthesizeToolSummary(
    toolSteps.map((t) => ({ toolName: t.toolName, input: t.input, output: t.output, errored: t.errored })),
  );

  // Count the SAME units the summary line's phrases are built from — a
  // batched call (imageSearchBatch, generateImageBatch) contributes its
  // weight (e.g. 3), not 1, so "3 image searches" can never sit next to
  // "2 STEPS" again (CHAT_UI_DESIGN_REVIEW.md §4 item 2).
  const nonToolStepCount = orderedSteps.length - toolSteps.length;
  const totalCount = countToolWorkUnits(
    toolSteps.map((t) => ({ toolName: t.toolName, input: t.input, output: t.output, errored: t.errored })),
  ) + nonToolStepCount;

  // Suppress the expand affordance when it would add literally nothing: a
  // single tool step, with no carried card, whose input/output has no
  // drilldown JSON — expanding would just re-print the summary line
  // verbatim ("Video clip generated — 2 STEPS" expanding to "Video clip
  // generated" again — CHAT_UI_DESIGN_REVIEW.md §4 §1.5 / "also worth doing").
  const onlyStep = orderedSteps.length === 1 ? orderedSteps[0] : undefined;
  const isRedundantSingleStep =
    !!onlyStep &&
    onlyStep.kind === 'tool' &&
    !onlyStep.card &&
    (() => {
      const delegated = resolveDelegatedToolDisplay(onlyStep.toolName, onlyStep.input, onlyStep.output);
      const inputJson = stringifyForDetails(delegated.input ?? onlyStep.input);
      const outputJson = stringifyForDetails(delegated.output ?? onlyStep.output);
      return !inputJson && !outputJson;
    })();

  // Timeline entry — NOT a card. A quiet single line in the conversation's
  // machinery register; expanding drops a hairline rail with the per-step
  // detail underneath. Restraint over ornament: no border box, no bg fill.
  return (
    <div className="text-sm" data-timeline-entry>
      {/*
        inline-flex + max-w-full (NOT flex w-full) so summary · count · caret
        stay one hugging cluster instead of stretching across the row — in
        wide layout (panel closed) `w-full` + `flex-1 truncate` on the
        summary pinned the count/caret ~900px away at the far right edge,
        and the row stopped reading as one object (CHAT_UI_DESIGN_REVIEW.md
        §4 item 3). The summary span keeps `min-w-0 truncate` so it still
        clips gracefully if the whole cluster can't fit.
      */}
      <button
        type="button"
        disabled={isRedundantSingleStep}
        onClick={isRedundantSingleStep ? undefined : () => setOpen((v) => !v)}
        aria-expanded={isRedundantSingleStep ? undefined : open}
        className={cn(
          'group/tl inline-flex max-w-full items-center gap-2.5 py-1 text-left',
          isRedundantSingleStep
            ? 'pointer-events-none cursor-default'
            : 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 rounded',
        )}
      >
        <CheckCircle
          weight="fill"
          aria-hidden="true"
          className={cn('size-3.5 shrink-0', hasError ? 'text-amber-500/70' : 'text-muted-foreground/50')}
        />
        <span
          data-timeline-summary
          className="min-w-0 truncate text-xs text-muted-foreground transition-colors group-hover/tl:text-foreground/85"
        >
          {summaryLine || (totalCount === 1 ? 'Worked through 1 step' : `Worked through ${totalCount} steps`)}
        </span>
        <span className="shrink-0 text-[10px] font-medium tabular-nums uppercase tracking-[1.5px] text-muted-foreground/60">
          {totalCount === 1 ? '1 step' : `${totalCount} steps`}
        </span>
        {!isRedundantSingleStep && (
          <CaretDown
            weight="bold"
            aria-hidden="true"
            className={cn('size-3 shrink-0 text-muted-foreground/40 transition-transform', open && 'rotate-180')}
          />
        )}
      </button>

      {open && (
        <div className="mt-0.5 ml-[6px] border-l border-border/50 pl-4">
          <ul className="space-y-0.5 py-1">
            {orderedSteps.map((s) => {
              if (s.kind === 'tool') {
                // Steps that carry their original chat card render it
                // directly — the full per-tool detail, exactly the node the
                // chat would have shown standalone.
                if (s.card) {
                  return (
                    <li key={s.key} className="py-1" data-timeline-card>
                      {s.card}
                    </li>
                  );
                }
                const Icon = iconFor(s.toolName);
                const delegated = resolveDelegatedToolDisplay(s.toolName, s.input, s.output);
                const inputJson = stringifyForDetails(delegated.input ?? s.input);
                const outputJson = stringifyForDetails(delegated.output ?? s.output);
                const hasDetails = Boolean(inputJson || outputJson);
                const isExpanded = !!expandedTools[s.key];
                return (
                  <li key={s.key} className="py-1 text-xs">
                    <button
                      type="button"
                      onClick={() => hasDetails && setExpandedTools((m) => ({ ...m, [s.key]: !m[s.key] }))}
                      aria-expanded={hasDetails ? isExpanded : undefined}
                      className={cn(
                        'flex w-full items-start gap-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 rounded',
                        hasDetails ? 'hover:text-foreground' : 'cursor-default',
                      )}
                    >
                      <Icon
                        weight="duotone"
                        className={cn('size-3.5 shrink-0 mt-0.5', s.errored ? 'text-amber-500/70' : 'text-muted-foreground/70')}
                      />
                      <span className="flex-1 min-w-0 text-muted-foreground">
                        {summarize(s.toolName, s.input, s.output)}
                        {s.errored ? <span className="ml-1 text-amber-500/75">(failed)</span> : null}
                      </span>
                      {hasDetails ? (
                        <CaretDown
                          weight="bold"
                          aria-hidden="true"
                          className={cn('size-3 shrink-0 mt-0.5 text-muted-foreground/45 transition-transform', isExpanded && 'rotate-180')}
                        />
                      ) : null}
                    </button>
                    {hasDetails && isExpanded ? (
                      <div className="mt-2 space-y-2 pl-5">
                        {inputJson ? (
                          <div className="space-y-1">
                            <p className="text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground/60">Input</p>
                            <CodeBlock code={inputJson} language="json" className="[&_pre]:max-h-72 [&_pre]:text-xs" />
                          </div>
                        ) : null}
                        {outputJson ? (
                          <div className="space-y-1">
                            <p className="text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground/60">Result</p>
                            <CodeBlock code={outputJson} language="json" className="[&_pre]:max-h-96 [&_pre]:text-xs" />
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </li>
                );
              }
              if (s.kind === 'note') {
                // Folded narration — a short aside the model said between
                // tool calls, merged into this group instead of splitting
                // it into a sibling entry. Quiet, non-expandable: it's
                // already short enough to read inline.
                return (
                  <li key={s.key} className="py-1 text-xs">
                    <div className="flex items-start gap-2 text-muted-foreground/75">
                      <span className="mt-1.5 size-1 shrink-0 rounded-full bg-muted-foreground/35" aria-hidden="true" />
                      <span className="flex-1 min-w-0 italic">{s.text}</span>
                    </div>
                  </li>
                );
              }
              // Reasoning step — own per-step expander
              const isExpanded = !!expandedReasoning[s.key];
              return (
                <li key={s.key} className="py-1 text-xs">
                  <button
                    type="button"
                    onClick={() => setExpandedReasoning((m) => ({ ...m, [s.key]: !m[s.key] }))}
                    aria-expanded={isExpanded}
                    className="flex w-full items-center gap-2 text-left text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 rounded"
                  >
                    <Brain weight="duotone" className="size-3.5 shrink-0 text-muted-foreground/55" aria-hidden="true" />
                    <span className="flex-1 min-w-0 italic text-muted-foreground/80">Thinking</span>
                    <CaretDown
                      weight="bold"
                      aria-hidden="true"
                      className={cn('size-3 shrink-0 text-muted-foreground/45 transition-transform', isExpanded && 'rotate-180')}
                    />
                  </button>
                  {isExpanded ? (
                    <div className="mt-1.5 pl-6 text-xs text-muted-foreground/75 [&_p]:leading-relaxed [&_p]:my-1 [&_ul]:my-1 [&_ul]:pl-4 [&_ol]:my-1 [&_ol]:pl-4 [&_:not(pre)>code]:rounded [&_:not(pre)>code]:bg-muted/40 [&_:not(pre)>code]:px-1 [&_:not(pre)>code]:text-[0.86em] [&_:not(pre)>code]:font-mono">
                      <Streamdown>{s.text}</Streamdown>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>

          {fullContent ? (
            <div className="border-t border-border/40 py-2">
              {fullContent}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
